import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@nrwl/environment';
import { map, Observable } from 'rxjs';
import { LaserEquipment } from './laser-equipment';
import { LaserProcedureType } from './laser-procedure-type';
import { LaserOption } from './laser-option';
import { LaserTreatment } from './laser-treatment';
import { Treatment } from './treatment';
import { Company } from './company';

/**
 * Parameters for fetching treatment details.
 */
export interface GetTreatmentParams {
  /**
   * Identifier of the appointment.
   */
  readonly appointmentId: string;
}

/**
 * Parameters for fetching laser treatment records.
 */
export interface GetLaserTreatmentsParams {
  /**
   * Identifier of the customer.
   */
  readonly customerId: string;
}

@Injectable({ providedIn: 'root' })
export class LaserService {
  private readonly http = inject(HttpClient);

  /**
   * Fetches PNE medical notes of type "Other" for a specific customer and treatment.
   */
  public getLaserTreatments(
    params: GetLaserTreatmentsParams,
  ): Observable<readonly LaserTreatment[]> {
    const { customerId } = params;

    const queryParams = { CustomerId: customerId };

    return this.http
      .get<unknown>(`${environment.baseAPIUrlMicro}egress-proxy/pne/treatmentlaser`, {
        params: queryParams,
      })
      .pipe(map((response) => this.parseLaserTreatmentsResponse(response)));
  }

  /**
   * Fetches laser equipment.
   */
  public getLaserEquipment(): Observable<readonly LaserEquipment[]> {
    return this.http
      .get<unknown>(`${environment.baseAPIUrlMicro}egress-proxy/pne/laserequipment`)
      .pipe(map((response) => this.parseLaserEquipmentResponse(response)));
  }

  /**
   * Fetches laser procedure types.
   */
  public getLaserProcedureTypes(): Observable<readonly LaserProcedureType[]> {
    return this.http
      .get<unknown>(`${environment.baseAPIUrlMicro}egress-proxy/pne/laserproceduretypes`)
      .pipe(map((response) => this.parseLaserProcedureTypesResponse(response)));
  }

  /**
   * Fetches treatment details for a specific appointment.
   */
  public getTreatment(params: GetTreatmentParams): Observable<Treatment> {
    const { appointmentId } = params;

    const queryParams = { legacyids: 'true' };

    return this.http
      .get<unknown>(
        `${environment.baseAPIUrlMicro}egress-proxy/pne/appointments/${appointmentId}/treatment/`,
        {
          params: queryParams,
        },
      )
      .pipe(map((response) => this.parseTreatmentResponse(response)));
  }

  /**
   * Fetches laser options.
   */
  public getLaserOptions(): Observable<readonly LaserOption[]> {
    return this.http
      .get<unknown>(`${environment.baseAPIUrlMicro}egress-proxy/pne/laseroption`)
      .pipe(map((response) => this.parseLaserOptionsResponse(response)));
  }

  /**
   * Fetches company details.
   */
  public getCompany(): Observable<Company> {
    return this.http
      .get<unknown>(`${environment.baseAPIUrlMicro}egress-proxy/pne/companies`)
      .pipe(map((response) => this.parseCompanyResponse(response)));
  }

  /**
   * Parses the laser treatments response into normalized structure.
   */
  private parseLaserTreatmentsResponse(response: unknown): readonly LaserTreatment[] {
    if (!Array.isArray(response)) {
      throw new Error('Invalid laser treatments response.');
    }

    return response.map((item) => this.parseLaserTreatmentRecord(item));
  }

  /**
   * Parses the laser equipment response into normalized structure.
   */
  private parseLaserEquipmentResponse(response: unknown): readonly LaserEquipment[] {
    if (!Array.isArray(response)) {
      throw new Error('Invalid laser equipment response.');
    }

    return response.map((item) => this.parseLaserEquipmentRecord(item));
  }

  /**
   * Parses the laser equipment response into normalized structure.
   */
  private parseLaserProcedureTypesResponse(response: unknown): readonly LaserProcedureType[] {
    if (!Array.isArray(response)) {
      throw new Error('Invalid laser procedure types response.');
    }

    return response.map((item) => this.parseLaserProcedureTypeRecord(item));
  }

  /**
   * Parses the laser options response into normalized structure.
   */
  private parseLaserOptionsResponse(response: unknown): readonly LaserOption[] {
    if (!Array.isArray(response)) {
      throw new Error('Invalid laser options response.');
    }

    return response.map((item) => this.parseLaserOptionRecord(item));
  }

  /**
   * Parses the company response into normalized structure.
   */
  private parseCompanyResponse(response: unknown): Company {
    if (!Array.isArray(response)) {
      throw new Error('Invalid company response.');
    }

    if (response.length === 0) {
      throw new Error('Empty company response.');
    }

    const item = response[0];

    if (!item || typeof item !== 'object') {
      throw new Error(`Invalid company payload.`);
    }

    const record = item as Record<string, unknown>;

    const smartCoolText = record['SmartCoolText'];
    const joulesText = record['JoulesText'];
    const pulseText = record['PulseText'];
    const repetitionRateText = record['RepRateText'];
    const firstLaserOptionText = record['LaserOption1'];
    const secondLaserOptionText = record['LaserOption2'];

    if (typeof smartCoolText !== 'string') {
      throw new Error('Invalid smart cool text.');
    }

    if (typeof joulesText !== 'string') {
      throw new Error('Invalid joules text.');
    }

    if (typeof pulseText !== 'string') {
      throw new Error('Invalid pulse text.');
    }

    if (typeof repetitionRateText !== 'string') {
      throw new Error('Invalid repetition rate text.');
    }

    if (typeof firstLaserOptionText !== 'string') {
      throw new Error('Invalid first laser option text.');
    }

    if (typeof secondLaserOptionText !== 'string') {
      throw new Error('Invalid second laser option text.');
    }

    return {
      smartCoolText: this.transformToNullableString(smartCoolText),
      joulesText: this.transformToNullableString(joulesText),
      pulseText: this.transformToNullableString(pulseText),
      repetitionRateText: this.transformToNullableString(repetitionRateText),
      firstLaserOptionText: this.transformToNullableString(firstLaserOptionText),
      secondLaserOptionText: this.transformToNullableString(secondLaserOptionText),
    };
  }

  /**
   * Parses the treatment response into normalized structure.
   */
  private parseTreatmentResponse(response: unknown): Treatment {
    const id = this.extractTreatmentId(response);
    if (!id) {
      throw new Error('Invalid treatment response.');
    }
    return { id };
  }

  private extractTreatmentId(response: unknown): string | null {
    if (response && typeof response === 'object' && !Array.isArray(response)) {
      const record = response as Record<string, unknown>;
      const treatmentId = record['treatmentId'] ?? record['TreatmentId'] ?? record['Id'];
      if (typeof treatmentId === 'string' || typeof treatmentId === 'number') {
        const val = String(treatmentId).trim();
        return val || null;
      }
    }

    if (Array.isArray(response) && response.length > 0) {
      const item = response[0];
      if (item && typeof item === 'object') {
        const id = (item as any)['Id'] ?? (item as any)['treatmentId'] ?? (item as any)['TreatmentId'];
        if (typeof id === 'string' || typeof id === 'number') {
          const val = String(id).trim();
          return val || null;
        }
      }
    }

    return null;
  }

  /**
   * Parses the laser treatment record into normalized structure.
   */
  private parseLaserTreatmentRecord(value: unknown): LaserTreatment {
    if (!value || typeof value !== 'object') {
      throw new Error('Invalid laser treatment record.');
    }

    const transformToNullableString = (value: string): string | null => {
      if (value.trim().length > 0) {
        return value;
      }

      return null;
    };

    const parseDate = (value: string): Date => {
      const date = new Date(value);

      if (isNaN(date.getTime())) {
        throw new Error('Invalid date.');
      }

      return date;
    };

    const record = value as Record<string, unknown>;

    const id = record['Id'];
    const date = record['TreatmentDate'];
    const treatmentId = record['TreatmentId'];
    const equipmentId = record['LaserEquipmentId'];
    const procedureTypeId = record['LaserProcedureTypeId'];
    const smartCool = record['SmartCool'];
    const joules = record['Joules'];
    const repetitionRate = record['RepRate'];
    const serviceTime = record['ServiceTime'];
    const pulse = record['LaserPulse'];
    const note = record['LaserNote'];
    const firstLaserOptionId = record['LaserOption1Id'];
    const secondLaserOptionId = record['LaserOption2Id'];

    if (typeof id !== 'string') {
      throw new Error('Invalid laser treatment ID.');
    }

    if (typeof date !== 'string') {
      throw new Error('Invalid laser treatment date.');
    }

    if (typeof treatmentId !== 'string') {
      throw new Error('Invalid laser treatment ID.');
    }

    if (typeof equipmentId !== 'string') {
      throw new Error('Invalid laser equipment ID.');
    }

    if (typeof procedureTypeId !== 'string') {
      throw new Error('Invalid laser procedure type ID.');
    }

    if (typeof smartCool !== 'number') {
      throw new Error('Invalid smart cool value.');
    }

    if (typeof joules !== 'number') {
      throw new Error('Invalid joules value.');
    }

    if (typeof repetitionRate !== 'number') {
      throw new Error('Invalid repetition rate value.');
    }

    if (typeof serviceTime !== 'number') {
      throw new Error('Invalid service time value.');
    }

    if (typeof pulse !== 'number') {
      throw new Error('Invalid pulse value.');
    }

    if (typeof note !== 'string') {
      throw new Error('Invalid laser note.');
    }

    if (typeof firstLaserOptionId !== 'string') {
      throw new Error('Invalid first laser option ID.');
    }

    if (typeof secondLaserOptionId !== 'string') {
      throw new Error('Invalid second laser option ID.');
    }

    return {
      id,
      treatmentId: transformToNullableString(treatmentId),
      date: parseDate(date),
      equipmentId: transformToNullableString(equipmentId),
      procedureTypeId: transformToNullableString(procedureTypeId),
      smartCool,
      joules,
      repetitionRate,
      serviceTime,
      pulse,
      note,
      firstLaserOptionId: transformToNullableString(firstLaserOptionId),
      secondLaserOptionId: transformToNullableString(secondLaserOptionId),
    };
  }

  /**
   * Parses the laser equipment record into normalized structure.
   */
  private parseLaserEquipmentRecord(value: unknown): LaserEquipment {
    if (!value || typeof value !== 'object') {
      throw new Error('Invalid laser equipment record.');
    }

    const record = value as Record<string, unknown>;

    const id = record['LaserEquipmentId'];
    const name = record['EquipmentName'];

    if (typeof id !== 'string') {
      throw new Error('Invalid laser equipment ID.');
    }

    if (typeof name !== 'string') {
      throw new Error('Invalid laser equipment name.');
    }

    return { id, name };
  }

  /**
   * Parses the laser equipment record into normalized structure.
   */
  private parseLaserProcedureTypeRecord(value: unknown): LaserProcedureType {
    if (!value || typeof value !== 'object') {
      throw new Error('Invalid laser procedure type record.');
    }

    const record = value as Record<string, unknown>;

    const id = record['LaserProcedureTypeId'];
    const name = record['ProcedureName'];

    if (typeof id !== 'string') {
      throw new Error('Invalid laser procedure type ID.');
    }

    if (typeof name !== 'string') {
      throw new Error('Invalid laser procedure type name.');
    }

    return { id, name };
  }

  /**
   * Parses the laser option record into normalized structure.
   */
  private parseLaserOptionRecord(value: unknown): LaserOption {
    if (!value || typeof value !== 'object') {
      throw new Error('Invalid laser option record.');
    }

    const record = value as Record<string, unknown>;

    const id = record['LaserOptionId'];
    const text = record['LaserOptionText'];
    const type = record['LaserOptionType'];

    if (typeof id !== 'string') {
      throw new Error('Invalid laser option ID.');
    }

    if (typeof text !== 'string') {
      throw new Error('Invalid laser option text.');
    }

    if (typeof type !== 'number') {
      throw new Error('Invalid laser option type.');
    }

    if (type !== 1 && type !== 2) {
      throw new Error('Invalid laser option type.');
    }

    return { id, text, type };
  }

  /**
   * Transforms a string to a nullable string.
   */
  private transformToNullableString(value: string): string | null {
    if (value.trim().length === 0) {
      return null;
    }

    return value;
  }
}
